import React from 'react';
import { Helmet } from 'react-helmet-async';
import BreadcrumbsSchema from '../components/BreadcrumbsSchema';
import { getSiteOrigin, joinUrl } from '../utils/seo';

function About() {
  const origin = React.useMemo(() => getSiteOrigin(), []);
  const canonical = React.useMemo(() => joinUrl(origin, '/about'), [origin]);
  const [progressReady, setProgressReady] = React.useState(false);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setProgressReady(true), 450);
    return () => window.clearTimeout(timer);
  }, []);

  const title = 'О платформе — Бюро Души';
  const description = 'Бюро Души — технологическая CRM-платформа для психологов и помогающих специалистов: запись, оплаты, видеосессии, аналитика и Aegis AI в одном кабинете.';
  const breadcrumbs = React.useMemo(() => ([
    { label: 'Главная', to: '/' },
    { label: 'О нас', to: '/about' },
  ]), []);

  const aboutSchema = React.useMemo(() => {
    if (!canonical) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'О платформе Бюро Души',
      url: canonical,
      inLanguage: 'ru-RU',
      about: {
        '@type': 'SoftwareApplication',
        name: 'Бюро Души',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
      },
    };
  }, [canonical]);

  return (
    <section className="b-about" id="about">
      <Helmet prioritizeSeoTags>
        <title>{title}</title>
        <meta name="description" content={description} />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="ru_RU" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {canonical ? <meta property="og:url" content={canonical} /> : null}
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {aboutSchema ? <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script> : null}
      </Helmet>

      <BreadcrumbsSchema items={breadcrumbs} />

      <div className="about-orb about-orb-1" />
      <div className="about-orb about-orb-2" />

      <div className="about-inner">
        <div className="about-header about-reveal about-reveal--visible">
          <div className="about-header-left">
            <div className="section-eyebrow">
              <span className="eyebrow-dot" />
              О платформе
            </div>
            <h1 className="about-title">
              Инструмент,<br />
              который думает<br />
              <span className="t-mint">за рутину.</span><br />
              <span className="t-lav">Не за вас.</span>
            </h1>
            <p className="about-sub">
              Бюро Души — технологическая CRM-платформа для психологов и помогающих специалистов. Мы автоматизируем всё, что мешает фокусироваться на клиенте.
            </p>
          </div>

          <div className="about-header-right" aria-label="Ключевые показатели платформы">
            <div className="metric-chip">
              <div className="metric-chip-icon mci-mint">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="9" cy="7" r="4" fill="#18A869" /><path d="M1 20c0-4 3.6-7 8-7s8 3 8 7" fill="#7EEFC0" /><circle cx="17" cy="8" r="3" fill="#18A869" opacity="0.5" /></svg>
              </div>
              <div>
                <div className="metric-chip-val"><span className="u-mint">+50</span></div>
                <div className="metric-chip-label">специалистов на платформе</div>
              </div>
            </div>
            <div className="metric-chip">
              <div className="metric-chip-icon mci-lav">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="2" y="6" width="20" height="14" rx="2" fill="#DDD8F8" /><circle cx="12" cy="13" r="2.5" fill="#7B6FD4" /></svg>
              </div>
              <div>
                <div className="metric-chip-val"><span className="u-lav">0%</span></div>
                <div className="metric-chip-label">комиссии с ваших доходов</div>
              </div>
            </div>
            <div className="metric-chip">
              <div className="metric-chip-icon mci-dark">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" fill="#D0D0D0" /><rect x="7" y="14" width="3" height="5" rx="0.5" fill="#888" opacity="0.6" /><rect x="11" y="10" width="3" height="9" rx="0.5" fill="#888" opacity="0.8" /><rect x="15" y="6" width="3" height="13" rx="0.5" fill="#888" /></svg>
              </div>
              <div>
                <div className="metric-chip-val">11</div>
                <div className="metric-chip-label">инструментов в одном кабинете</div>
              </div>
            </div>
          </div>
        </div>

        <div className="about-grid">
          <div className="dashboard-block about-reveal about-reveal--visible">
            <div className="db-header">
              <div className="wdot wd-r" />
              <div className="wdot wd-y" />
              <div className="wdot wd-g" />
              <div className="db-title">Бюро Души — Кабинет специалиста</div>
            </div>
            <div className="db-body">
              <div className="db-stats-row">
                <div className="db-stat"><div className="db-stat-label">Клиентов</div><div className="db-stat-val c-mint">24</div></div>
                <div className="db-stat"><div className="db-stat-label">Сессий/мес</div><div className="db-stat-val">38</div></div>
                <div className="db-stat"><div className="db-stat-label">Слоты</div><div className="db-stat-val c-lav">6</div></div>
              </div>

              <div className="db-progress-block">
                <div className="db-progress-head">
                  <span className="db-progress-label">Доход за месяц</span>
                  <span className="db-progress-pct">76 400 ₽</span>
                </div>
                <div className="progress-track"><div className="progress-fill" style={{ width: progressReady ? '78%' : '0%' }} /></div>
              </div>

              <div className="db-clients">
                <div className="db-client-item"><div className="db-avatar db-avatar--mint">АК</div><div className="db-client-name">Алина Кротова</div><div className="db-client-time">Сегодня · 14:00</div><div className="db-badge badge-soon">Скоро</div></div>
                <div className="db-client-item"><div className="db-avatar db-avatar--lav">ДМ</div><div className="db-client-name">Дмитрий Морозов</div><div className="db-client-time">16:30</div><div className="db-badge badge-note">Новый</div></div>
                <div className="db-client-item"><div className="db-avatar db-avatar--dark">МС</div><div className="db-client-name">Мария Соколова</div><div className="db-client-time">Вчера</div><div className="db-badge badge-done">Завершено</div></div>
              </div>

              <div className="db-aegis">
                <div className="aegis-dot-wrap">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4z" fill="rgba(255,255,255,0.9)" /></svg>
                </div>
                <div><div className="aegis-label">Aegis AI</div><div className="aegis-sub">Расшифровка сессии · 1 файл в обработке</div></div>
                <div className="aegis-status">Активен</div>
              </div>
            </div>
          </div>

          <div className="about-right-col">
            <div className="philosophy-card about-reveal about-reveal--visible">
              <div className="phi-eyebrow">Наш подход</div>
              <div className="phi-quote">Рутина — машине.<br /><span className="q-lav">Клиент — вам.</span></div>
              <p className="phi-desc">
                Психологи тратят до 20% рабочего времени на организационные задачи. Мы убрали это. Записи, напоминания, оплаты, аналитика — всё работает само. Вы сосредотачиваетесь на том, зачем пришли в профессию.
              </p>
              <div className="phi-tags">
                <span className="phi-tag">Автоматизация</span>
                <span className="phi-tag">Безопасность данных</span>
                <span className="phi-tag">Без комиссий</span>
                <span className="phi-tag">152-ФЗ</span>
                <span className="phi-tag">Один кабинет</span>
              </div>
            </div>

            <div className="founders-card about-reveal about-reveal--visible">
              <div className="founders-content">
                <div className="founders-label">Команда</div>
                <div className="founders-row">
                  <div className="founder-pill"><div className="founder-avatar founder-avatar--mint">ВК</div><div><div className="founder-name">Валерия К.</div><div className="founder-role">Продукт</div></div></div>
                  <div className="founder-pill"><div className="founder-avatar founder-avatar--lav">ВЛ</div><div><div className="founder-name">Владислав Л.</div><div className="founder-role">Технологии</div></div></div>
                </div>
                <div className="founders-tagline">Строим платформу,<br />которой сами хотели бы <span className="ft-mint">пользоваться.</span></div>
                <div className="founders-note">Психологическая практика заслуживает нормального инструмента — не таблиц и мессенджеров.</div>
              </div>
            </div>
          </div>
        </div>

        <div className="about-bottom">
          <div className="feature-pill-card about-reveal about-reveal--visible">
            <div className="fpc-top"><div className="fpc-icon fpc-icon-mint"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="6" width="18" height="15" rx="2" fill="#7EEFC0" /><rect x="3" y="6" width="18" height="6" rx="2" fill="#18A869" /><rect x="8" y="2" width="2" height="5" rx="1" fill="#18A869" /><rect x="14" y="2" width="2" height="5" rx="1" fill="#18A869" /></svg></div><div className="fpc-badge fpb-mint">Запись</div></div>
            <div className="fpc-name">Умный календарь</div><div className="fpc-desc">Клиент сам выбирает время. Напоминания — автоматически.</div>
          </div>
          <div className="feature-pill-card about-reveal about-reveal--visible">
            <div className="fpc-top"><div className="fpc-icon fpc-icon-lav"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="2" y="6" width="14" height="12" rx="2" fill="#7EEFC0" /><path d="M16 9.5l6-3.5v12l-6-3.5V9.5z" fill="#18A869" /></svg></div><div className="fpc-badge fpb-lav">HD</div></div>
            <div className="fpc-name">Видеосессии</div><div className="fpc-desc">Встроен в платформу. Zoom не нужен.</div>
          </div>
          <div className="feature-pill-card about-reveal about-reveal--visible">
            <div className="fpc-top"><div className="fpc-icon fpc-icon-mint"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" fill="#7EEFC0" /><rect x="7" y="14" width="3" height="5" rx="0.5" fill="#18A869" opacity="0.5" /><rect x="11" y="10" width="3" height="9" rx="0.5" fill="#18A869" opacity="0.75" /><rect x="15" y="6" width="3" height="13" rx="0.5" fill="#18A869" /></svg></div><div className="fpc-badge fpb-mint">Данные</div></div>
            <div className="fpc-name">Финансы и аналитика</div><div className="fpc-desc">100% дохода ваш. Отчёты сами считаются.</div>
          </div>
          <div className="feature-pill-card about-reveal about-reveal--visible">
            <div className="fpc-top"><div className="fpc-icon fpc-icon-dark"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4z" fill="#C0C0C0" /><path d="M12 2l1.5 4.7L18 8.5l-4.5 1.5L12 15l-1.5-5-4.5-1.5 4.5-1.5L12 2z" fill="#888" /></svg></div><div className="fpc-badge fpb-grey">AI</div></div>
            <div className="fpc-name">Aegis AI</div><div className="fpc-desc">Транскрипция и резюме сессий. Автоматически.</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default About;
